import { Middleware } from "@reduxjs/toolkit";
import { RootState } from "../index";
import { setStateroomConfig } from "../features/shipBuilderSlice";
import { selectAssignedCrewCount } from "../selectors/derivedSelectors";
import { STATEROOM_TYPES } from "../../constants/stateroom";

/**
 * Middleware to automatically assign staterooms based on crew count and percentage
 * This middleware watches for changes to:
 * - Crew assignments
 * - Stateroom percentage
 * - Auto-assign toggle
 * 
 * When auto-assign is enabled, it automatically calculates and updates:
 * - Single staterooms to accommodate crew (based on percentage)
 * - Common areas (2 tons per 10 staterooms, as per Traveller rules)
 */
export const stateroomAutoAssignMiddleware: Middleware<{}, RootState> = (store) => (next) => (action: any) => {
  // First, let the action pass through
  const result = next(action);
  
  // Debug logging (can be removed in production)
  // console.log('🏠 stateroomAutoAssignMiddleware: Action dispatched:', action.type);

  // Get the current state after the action
  const state = store.getState();
  const stateroomConfig = state.shipBuilder.stateroomConfig;

  // Only proceed if auto-assignment is enabled
  if (!stateroomConfig?.autoAssignStaterooms) {
    return result;
  }

  // Actions that should trigger stateroom recalculation
  const triggerActions = [
    // Direct crew assignment actions
    "shipBuilder/setCrewComposition",
    "shipBuilder/setCrewOverrides", // This is the actual action used by the crew UI
    "shipBuilder/setCrewMinimums",
    "shipBuilder/setCrewOverride",
    
    // Stateroom configuration actions
    "shipBuilder/setStateroomPercentage",
    "shipBuilder/setStateroomAutoAssign",
    "shipBuilder/setPassengerCount",
    "shipBuilder/setPassengerCargoOverride", // Passenger cargo overrides affect calculations
    
    // Ship configuration actions that affect crew requirements
    "shipBuilder/setShipSize", // Ship size affects crew requirements
    "shipBuilder/setDrives", // Drives affect engineer requirements
    "shipBuilder/setPowerPlant", // Power plant affects engineer requirements
    "shipBuilder/setWeapons", // Weapons affect gunner requirements
    "shipBuilder/addWeapon", // Adding weapons affects gunner requirements
    "shipBuilder/removeWeapon", // Removing weapons affects gunner requirements
    "shipBuilder/addWeaponMount", // Adding weapon mounts affects gunner requirements
    "shipBuilder/removeWeaponMount", // Removing weapon mounts affects gunner requirements
    "shipBuilder/setSmallCraft", // Small craft affect pilot/engineer requirements
    
    // NOTE: We do NOT include updateShipStats here to avoid infinite loops
    // updateShipStats is dispatched by shipStatsMiddleware and would cause a loop
  ];

  if (triggerActions.includes(action.type)) {
    // Debug logging (can be removed in production)
    // console.log('🏠 stateroomAutoAssignMiddleware: Trigger action detected:', action.type);
    // console.log('🏠 stateroomAutoAssignMiddleware: Auto-assign enabled:', stateroomConfig?.autoAssignStaterooms);
    
    // Get assigned crew count and passenger counts
    const assignedCrewCount = selectAssignedCrewCount(state);
    // console.log('🏠 stateroomAutoAssignMiddleware: Assigned crew count:', assignedCrewCount);
    const percentage = stateroomConfig.stateroomPercentage ?? 100;
    const passengerConfig = state.shipBuilder.passengerConfig || {
      high: 0, middle: 0, basic: 0, low: 0
    };

    // Calculate accommodation requirements according to Traveller rules:
    // - Crew: Based on percentage of assigned crew
    // - High/Middle Passengers: Each requires 1 single stateroom
    // - Basic Passengers: 2 passengers = 1 double occupancy stateroom
    // - Low Passengers: Each requires 1 low berth

    const crewStaterooms = Math.ceil((assignedCrewCount * percentage) / 100);
    const highPassengerStaterooms = passengerConfig.high;
    const middlePassengerStaterooms = passengerConfig.middle;
    const basicPassengerDoubleStaterooms = Math.ceil(passengerConfig.basic / 2);
    const lowPassengerLowBerths = passengerConfig.low;

    // Calculate total single staterooms needed
    const totalSingleStaterooms = crewStaterooms + highPassengerStaterooms + middlePassengerStaterooms;

    // Calculate recommended common areas (25% of stateroom tonnage, matching existing system)
    const stateroomTons = 4; // Standard stateroom tonnage
    const totalStateroomsForCommonArea = totalSingleStaterooms + basicPassengerDoubleStaterooms;
    const recommendedCommonArea = Math.ceil(totalStateroomsForCommonArea * stateroomTons * 0.25);

    // Calculate total cost, tonnage, and power
    const singleStateroomCost = totalSingleStaterooms * STATEROOM_TYPES.STANDARD.cost;
    const singleStateroomTons = totalSingleStaterooms * STATEROOM_TYPES.STANDARD.tons;
    const singleStateroomPower = totalSingleStaterooms * STATEROOM_TYPES.STANDARD.power;

    const doubleStateroomCost = basicPassengerDoubleStaterooms * STATEROOM_TYPES.STANDARD.cost;
    const doubleStateroomTons = basicPassengerDoubleStaterooms * STATEROOM_TYPES.STANDARD.tons;
    const doubleStateroomPower = basicPassengerDoubleStaterooms * STATEROOM_TYPES.STANDARD.power;
    
    const commonAreaCost = recommendedCommonArea * 10000; // Common area is 10,000 Cr/ton
    const commonAreaPower = 0; // Common area has no power requirement

    // Calculate low berth costs and requirements
    const lowBerthCost = lowPassengerLowBerths * 50000; // 50,000 Cr per low berth
    const lowBerthTons = lowPassengerLowBerths * 0.5; // 0.5 tons per low berth
    const lowBerthPower = Math.ceil(lowPassengerLowBerths / 10); // 1 power per 10 low berths

    // Keep existing values for things we don't auto-assign
    const existingEmergencyLowBerths = stateroomConfig.emergencyLowBerths;

    // Calculate total with all accommodation types
    const totalCost =
      singleStateroomCost +
      doubleStateroomCost +
      lowBerthCost +
      commonAreaCost +
      (existingEmergencyLowBerths?.count || 0) * 25000; // Emergency low berths

    const totalTons =
      singleStateroomTons +
      doubleStateroomTons +
      lowBerthTons +
      recommendedCommonArea +
      (existingEmergencyLowBerths?.count || 0) * 0.5; // Emergency low berths

    const totalPower =
      singleStateroomPower +
      doubleStateroomPower +
      lowBerthPower +
      commonAreaPower +
      (existingEmergencyLowBerths?.count || 0) * 0; // Emergency low berths have no power

    // Update the stateroom configuration
    // console.log('🏠 stateroomAutoAssignMiddleware: Updating stateroom config with:', {
    //   single: totalSingleStaterooms,
    //   double: basicPassengerDoubleStaterooms,
    //   lowBerths: lowPassengerLowBerths,
    //   commonArea: recommendedCommonArea,
    //   totalCost,
    //   totalTons,
    //   totalPower
    // });
    
    store.dispatch(
      setStateroomConfig({
        ...stateroomConfig,
        single: totalSingleStaterooms,
        double: basicPassengerDoubleStaterooms,
        lowBerths: {
          ...stateroomConfig.lowBerths,
          count: lowPassengerLowBerths,
          cost: lowBerthCost,
          tons: lowBerthTons,
          power: lowBerthPower,
          capacity: lowPassengerLowBerths, // 1 person per low berth
          techLevel: 8,
          maintenanceCost: 0,
        },
        commonArea: recommendedCommonArea,
        totalCost,
        totalTons,
        totalPower,
        warnings: [],
      })
    );
  }

  return result;
};

